/**
 * Helpers for extracting user-facing error messages from errors thrown by
 * the SPA's `apiClient` (see `api-client.ts`).
 *
 * Why this file exists
 * --------------------
 * PR #327 (H4) made `api-client.request<T>()` enrich `Error.message` for
 * VALIDATION_ERROR responses — it now expands the generic "Validation
 * failed" into a human-readable field-by-field summary like
 * `"name: Required; framework: Must be one of …"`.
 *
 * However, the SPA was peppered with call sites of the form
 *
 *     const msg = e.data?.error?.message ?? e.message ?? 'Foo failed';
 *
 * which reads the RAW server message ("Validation failed") FIRST and only
 * falls back to the enriched `e.message` if the server omitted a message.
 * That means PR #327's enrichment was silently bypassed in every dialog,
 * form, and route that followed this pattern — the founder's reported
 * "popup shows literal 'Validation failed'" bug persisted in 23 files.
 *
 * Audit M1 from PR #327 documented these sites. This helper centralizes
 * the message-extraction priority so future updates only need to touch
 * one file.
 *
 * Priority
 * --------
 * 1. For VALIDATION_ERROR (detected via `e.data.error.code`), prefer the
 *    enriched `e.message` — the api-client built it from `fieldErrors`
 *    and the user needs that detail.
 * 2. Otherwise, prefer the server-supplied `e.data.error.message` (it
 *    typically includes resource-specific context the client can't
 *    reconstruct, e.g. "User with email alice@x exists").
 * 3. Fall back to the generic `e.message`.
 * 4. Finally, the caller-supplied fallback string.
 */

/** Shape of the error thrown by `apiClient.request<T>()`. */
export interface ApiClientError {
  status?: number;
  message?: string;
  data?: {
    error?: {
      code?: string;
      message?: string;
      details?: {
        fieldErrors?: Array<{ path: string; message: string; code?: string }>;
        [key: string]: unknown;
      };
      [key: string]: unknown;
    };
  };
  fieldErrors?: Array<{ path: string; message: string; code?: string }>;
}

/**
 * Extract a user-facing error message from an error thrown by `apiClient`.
 *
 * For `VALIDATION_ERROR` responses this returns the api-client's enriched
 * message (which expands `fieldErrors` into a readable list). For all
 * other errors the server's own `error.message` is preferred — those
 * tend to carry domain-specific context worth showing verbatim.
 *
 * @param err     The thrown error (anything; non-Errors are tolerated)
 * @param fallback Message to use when neither the error nor server body
 *                 carry a message
 */
export function getApiErrorMessage(err: unknown, fallback: string): string {
  const e = err as ApiClientError | undefined;

  if (e?.data?.error?.code === 'VALIDATION_ERROR' && e.message) {
    // PR #327 path: api-client built `e.message` from `fieldErrors`.
    // The raw `e.data.error.message` here is just "Validation failed".
    return e.message;
  }

  return e?.data?.error?.message ?? e?.message ?? fallback;
}

/** Extract the API error code (e.g. `VALIDATION_ERROR`, `EMAIL_EXISTS`). */
export function getApiErrorCode(err: unknown): string {
  const e = err as ApiClientError | undefined;
  return e?.data?.error?.code ?? 'UNKNOWN';
}

/** True when an API error represents an authenticated permission boundary. */
export function isPermissionDeniedApiError(err: unknown): boolean {
  const e = err as ApiClientError | undefined;
  const status = e?.status;
  if (status === 403) return true;

  const code = e?.data?.error?.code;
  if (code === 'FORBIDDEN' || code === 'PERMISSION_DENIED') return true;

  const serverMessage = e?.data?.error?.message ?? '';
  const message = e?.message ?? '';
  return /\b403\b/.test(`${message} ${serverMessage}`);
}
